import { useEffect, useRef, useState } from "react";
import { FileUp, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Field, inputCls } from "@/components/AuthShell";
import { guessTitle, LEVELS, SUBJECTS } from "@/lib/api";

const empty = { title: "", author: "", subject: "arabic", level: 1 };

export const BookDialog = ({ open, onOpenChange, initial, onSubmit, saving, uploadPct }) => {
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const inputRef = useRef(null);
  const editing = Boolean(initial?.id);

  useEffect(() => {
    if (open) { setForm(initial ? { ...empty, ...initial } : empty); setFile(null); setFileErr(""); }
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pick = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) { setFileErr("يرجى اختيار ملف PDF"); return; }
    if (f.size > 50 * 1024 * 1024) { setFileErr("الحد الأقصى لحجم الملف 50 ميغابايت"); return; }
    setFileErr(""); setFile(f);
    if (!editing) setForm((s) => ({ ...s, title: guessTitle(f.name) }));
  };
  const submit = (e) => {
    e.preventDefault();
    if (!editing && !file) { setFileErr("اختر ملف PDF أولًا"); return; }
    onSubmit({ ...form, level: Number(form.level) }, file);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF7F2] max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="admin-book-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-right">{editing ? "تعديل بيانات الكتاب" : "رفع كتاب جديد"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-book-form">
          {!editing && (
            <div>
              <button type="button" onClick={() => inputRef.current?.click()} data-testid="admin-book-file-btn"
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
                className="w-full border-2 border-dashed border-[#E5DCCB] hover:border-[#8B3A2B] bg-white p-6 text-center transition-colors">
                {file ? (
                  <span className="flex items-center justify-center gap-2 text-sm font-semibold"><FileText className="w-5 h-5 text-[#8B3A2B]" /> {file.name} · {(file.size / 1048576).toFixed(1)} MB</span>
                ) : (
                  <span className="flex flex-col items-center gap-2 text-sm text-stone-600"><FileUp className="w-7 h-7 text-[#8B3A2B]" /> اسحب ملف PDF هنا أو اضغط للاختيار (حتى 50 ميغابايت)</span>
                )}
              </button>
              <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="admin-book-file-input" />
              {fileErr && <p className="text-sm text-red-800 mt-2" data-testid="admin-book-file-error">{fileErr}</p>}
            </div>
          )}
          {editing && <Field label="عنوان الكتاب (اختياري)"><input className={inputCls} value={form.title || ""} onChange={set("title")} dir="auto" data-testid="admin-book-title" /></Field>}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="المادة">
              <select className={inputCls} value={form.subject} onChange={set("subject")} data-testid="admin-book-subject">
                {SUBJECTS.map((s) => <option key={s.id} value={s.id}>{s.ar}</option>)}
              </select>
            </Field>
            <Field label="المستوى">
              <select className={inputCls} value={form.level} onChange={set("level")} data-testid="admin-book-level">
                {LEVELS.map((l) => <option key={l.n} value={l.n}>{l.n} · {l.name}</option>)}
              </select>
            </Field>
          </div>
          {saving && !editing && <Progress value={uploadPct} className="h-2" data-testid="admin-book-upload-progress" />}
          <button disabled={saving} className="w-full h-12 rounded-full bg-[#1C1917] text-[#FAF7F2] font-semibold hover:bg-[#8B3A2B] transition-colors disabled:opacity-60" data-testid="admin-book-submit-btn">
            {saving ? (editing ? "جارٍ الحفظ..." : `جارٍ الرفع ${uploadPct}%`) : editing ? "حفظ التعديلات" : "رفع الكتاب"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
